import React from 'react';

import { createBrowserRouter, Navigate } from 'react-router-dom';
import { ModalLayout, SystemLayout } from './components';

import {
  ClanProfileView,
  ClansView,
  CookiePolicyView,
  DataRemoveFormView,
  ErrorView,
  HomeView,
  HowItWorksView,
  PrivacyPolicyView,
  ProfileView,
  SettingsView,
  TasksView,
  TermsAndConditionsView,
} from './views';
import { ClanInvites } from '@/views/clan-profile/clan-invites';
import { ClanSettings } from '@/views/clan-profile/clan-settings';
import DailyRewardsView from '@/views/daily-reward/DailyReward.view';
import SetWalletComponent from '@/views/settings/rewards/SetWallet.component';
import { AppLayout, SecureLayout } from '@/components';
import LoginView from '@/views/auth/login/Login.view';
import RegisterView from '@/views/auth/register/Register.view';
import RecoveryView from '@/views/auth/recovery/Recovery.view';
import VerifyView from '@/views/auth/verify/Verify.view';
import { ResetView } from '@/views/auth';
import { ChangeEmailView } from '@/views/settings/account/ChangeEmail.view';
import { ChangePasswordView } from '@/views/settings/account/ChangePassword.view';
import { ChangeUsernameView } from '@/views/settings/account/ChangeUsername.view';
import { default as ChangeAvatarView } from '@/views/settings/account/ChangeAvatar.view';
import { EmailVerifyView } from '@/views/settings/account/EmailVerify.view';
import { LogoutView } from '@/views/auth/logout';
import KeplrConnectionView from '@/views/auth/connect/KeplrConnection.view';
import LeapConnectionView from '@/views/auth/connect/LeapConnection.view';
import { AirdropLogin } from './views/home/airdrop-login/AirdropLogin.view';
import { AirdropHome } from './views/home/airdrop-home/AirdropHome.view';
// import { RecentActivities } from './views/settings/recent-activities';

const routes = createBrowserRouter([
  {
    element: <SystemLayout />,
    errorElement: <ErrorView />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            element: <HomeView />,
            children: [
              {
                index: true,
                element: <></>,
              },
              {
                path: 'daily',
                element: <ModalLayout />,
                children: [
                  {
                    index: true,
                    element: <DailyRewardsView />,
                  },
                ],
              },
            ],
          },
          {
            path: 'airdrop',
            element: <AirdropLogin />,
          },
          {
            path: 'how-it-works',
            element: <HowItWorksView />,
          },
          {
            path: 'tos',
            element: <TermsAndConditionsView />,
          },
          {
            path: 'privacy',
            element: <PrivacyPolicyView />,
          },
          { path: 'cookies', element: <CookiePolicyView /> },
          {
            path: 'data-remove-form',
            element: <DataRemoveFormView />,
          },
          {
            path: 'login',
            element: <LoginView />,
          },
          {
            path: 'logout',
            element: <LogoutView />,
          },
          {
            path: 'recovery',
            element: <RecoveryView />,
          },
          {
            path: 'register',
            element: <RegisterView />,
          },
          {
            path: 'reset',
            element: <ResetView />,
          },
          {
            path: 'verify',
            element: <VerifyView />,
          },

          {
            path: 'campaigns',
            children: [
              {
                index: true,
                lazy: () => import('./views/campaigns/Campaigns.view'),
              },
              {
                path: ':id',
                children: [
                  {
                    index: true,
                    lazy: () => import('./views/campaigns/CampaignDetails.view'),
                  },
                ],
              },
            ],
          },

          {
            element: <SecureLayout check={(roles) => roles?.includes('user')} />,
            children: [
              {
                path: 'airdrop-home',
                element: <AirdropHome />,
              },
              {
                path: 'tasks',
                element: <TasksView />,
              },
              {
                path: 'projects',
                children: [
                  {
                    index: true,
                    lazy: () => import('./views/projects/Projects.view'),
                  },
                  {
                    path: ':id',
                    children: [
                      {
                        index: true,
                        lazy: () => import('./views/projects/Project.view'),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'clans',
                children: [
                  {
                    index: true,
                    element: <ClansView />,
                  },
                  {
                    path: 'invites',
                    element: <ClansView mode='invites' />,
                  },
                  {
                    path: 'join-requests',
                    element: <ClansView mode='join-requests' />,
                  },
                  {
                    path: ':guildId/invites',
                    element: <ClanProfileView mode='invites' />,
                  },
                  {
                    path: ':guildId/join-requests',
                    element: <ClanProfileView mode='join-requests' />,
                  },
                  {
                    path: ':guildId',
                    element: <ClanProfileView />,
                    // errorElement: <Navigate to='/clans' />,
                    children: [
                      {
                        path: 'settings',
                        element: <ClanSettings />,
                        children: [
                          {
                            index: true,
                            element: <Navigate to='message' />,
                          },
                          {
                            path: 'message',
                            element: <ClanSettings.Message />,
                          },
                          {
                            path: 'avatar',
                            element: <ClanSettings.Avatar />,
                          },
                          // {
                          //   path: 'applications',
                          //   element: <ClanSettings.Applications />,
                          // },
                          {
                            path: 'roles',
                            element: <ClanSettings.Roles />,
                          },
                        ],
                      },
                      {
                        path: 'invite',
                        element: <ModalLayout />,
                        children: [
                          {
                            index: true,
                            element: <ClanInvites />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                path: 'leaderboards',
                children: [
                  {
                    index: true,
                    element: <Navigate to='players' />,
                  },
                  {
                    path: 'players',
                    lazy: () => import('./views/leaderboard/PlayerLeaderboard.view'),
                    // element: <PlayerLeaderboardView />,
                  },
                  {
                    path: 'clans',
                    lazy: () => import('./views/leaderboard/ClanLeaderboard.view'),
                    // element: <ClanLeaderboardView />,
                  },
                  {
                    path: '*',
                    element: <ErrorView status={404} />,
                  },
                ],
              },
            ],
          },
          {
            element: (
              <SecureLayout
                check={(roles) => roles?.includes('user') || roles?.includes('newbie')}
              />
            ),
            children: [
              {
                path: 'profile',
                element: <ProfileView />,
              },
              {
                path: 'settings',
                element: <SettingsView />,
                children: [
                  {
                    index: true,
                    element: <SettingsView.General />,
                  },
                  {
                    path: 'activities',
                    element: <SettingsView.Activities />,
                  },
                  {
                    path: 'rewards',
                    element: <SettingsView.Rewards />,
                    children: [
                      {
                        path: '*',
                        element: <ModalLayout />,
                        children: [
                          {
                            path: ':id',
                            element: <SetWalletComponent />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'connect',
                    element: <SettingsView.Connections />,
                  },
                  {
                    path: 'profile',
                    element: <SettingsView.Profile />,
                  },
                  {
                    path: 'account/email',
                    element: <ChangeEmailView />,
                    children: [
                      {
                        path: '*',
                        element: <ModalLayout />,
                        children: [
                          {
                            path: ':email',
                            element: <EmailVerifyView />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: 'account/avatar',
                    element: <ChangeAvatarView />,
                  },

                  {
                    path: 'account/password',
                    element: <ChangePasswordView />,
                  },
                  {
                    path: 'account/username',
                    element: <ChangeUsernameView />,
                  },
                ],
              },
            ],
          },
          {
            path: 'connect/metamask',
            lazy: () => import('@/views/auth/connect/MetamaskConnection.view'),
          },
          {
            path: 'connect/keplr',
            element: <KeplrConnectionView />,
          },
          {
            path: 'connect/leap',
            element: <LeapConnectionView />,
          },
          {
            path: 'connect/twitter',
            lazy: () => import('@/views/auth/connect/TwitterConnection.view'),
          },
          {
            path: 'connect/twitter/success',
            lazy: () => import('@/views/auth/connect/TwitterConnectionSuccess.view'),
          },
          {
            path: 'connect/google',
            lazy: () => import('@/views/auth/connect/GoogleConnection.view'),
          },
          {
            path: 'connect/google/success',
            lazy: () => import('@/views/auth/connect/GoogleConnectionSuccess.view'),
          },
          {
            path: 'connect/discord',
            lazy: () => import('@/views/auth/connect/DiscordConnection.view'),
          },
          {
            path: 'connect/discord/success',
            lazy: () => import('@/views/auth/connect/DiscordConnectionSuccess.view'),
          },
          {
            path: 'connect/telegram',
            lazy: () => import('@/views/auth/connect/TelegramConnection.view'),
          },
          {
            path: 'connect/github',
            lazy: () => import('@/views/auth/connect/GithubConnection.view'),
          },
          {
            path: 'connect/github/success',
            lazy: () => import('@/views/auth/connect/GithubConnectionSuccess.view'),
          },
          {
            path: 'connect/steam',
            lazy: () => import('@/views/auth/connect/SteamConnection.view'),
          },
          {
            path: 'connect/steam/success',
            lazy: () => import('@/views/auth/connect/SteamConnectionSuccess.view'),
          },
          {
            path: 'connect/reddit',
            lazy: () => import('@/views/auth/connect/RedditConnection.view'),
          },
          {
            path: 'connect/reddit/success',
            lazy: () => import('@/views/auth/connect/RedditConnectionSuccess.view'),
          },
          {
            path: 'connect/spotify',
            lazy: () => import('@/views/auth/connect/SpotifyConnection.view'),
          },
          {
            path: 'connect/spotify/success',
            lazy: () => import('@/views/auth/connect/SpotifyConnectionSuccess.view'),
          },
          {
            path: '*',
            element: <ErrorView status={404} />,
          },
        ],
      },
    ],
  },
]);

export default routes;
